/**
 * incidents-loader.ts — THE ONE PLACE incidents are loaded.
 *
 * PostGIS cutover: fetches `/api/incidents-live/{city}` (Cloudflare
 * Worker) first. If the Worker itself is down, the app falls back to the
 * latest static PostGIS snapshot shipped with the site, so a Cloudflare
 * 1101 never turns the live map blank.
 * The route is now backed by PostgreSQL + PostGIS:
 *
 *   incident table       → geography(POINT,4326)
 *   city-wide live feed  → /api/incidents-live/{city}
 *   spatial queries      → ST_DWithin/ST_Distance in the engine APIs
 *   total failure        → empty-but-valid JSON shape, never null
 *
 * That means client code is dramatically simpler than it used to be —
 * one fetch, one cache layer (the Worker's own Cache API + the browser
 * cache-control: max-age=300 it returns), and we trust the response.
 *
 * Why a single endpoint instead of static + live fallback:
 *   The previous architecture chained "static JSON shipped with Pages
 *   deploy" → "Pages Function for live fetch on staleness" → "GitHub
 *   Actions cron for refresh". Each layer had its own failure mode,
 *   and the user's map went stale whenever any single tier broke
 *   (token expiry, parse error, GH queue lag). The PostGIS worker
 *   consolidates the data plane behind one API and one database.
 *
 * Contract (unchanged from the caller's perspective):
 *   - Cached per city with a 15-minute TTL (the engine ingest/pulse crons
 *     run `*\/15`, so anything fresher would only re-read the same rows).
 *     Within the TTL, calls return the cached feed; past it, the next
 *     call refetches and swaps atomically — the old feed keeps serving
 *     until the new one is ready.
 *   - Cross-module reuse: the verdict card, click-card, map bubbles,
 *     and near-me panel all see consistent deduped counts.
 *   - Network failure is non-fatal — returns sentinel `LoadedFeed`
 *     with empty arrays + `ready: false`. The sentinel is cached for
 *     30s only (a transient failure must not poison the session), and
 *     it NEVER evicts a previously-good feed.
 *
 * Verification logging:
 *   On successful load, emits one structured `console.log` per city
 *   showing raw vs deduped counts so a reviewer can prove dedup fired.
 */

import {
  deduplicateIncidents,
  type DedupedIncident,
  type RawIncident,
} from "./incident-dedup";
import { fetchFacebookItems } from "./facebook-loader";
import { engineBase } from "./trust-report-loader";

export type CityKey = "hyd" | "mel" | "blr" | "syd";

/**
 * Cities whose live feed comes from the TRUTH ENGINE (pulse worker,
 * Postgres/PostGIS, `pg-live-v1` schema) rather than the RSS cron worker.
 * The engine payload is shape-compatible with the cron payload AND carries
 * the signal contract per item: trust_tier, confidence_pct,
 * evidence_status, confidence_breakdown. Sydney first — extend as cities'
 * engine sources light up (mel → hyd → blr per the locked build order).
 */
const ENGINE_FEED_CITIES = new Set<CityKey>(["syd"]);

/**
 * Full result handed back to every caller. Everything anyone could need
 * about "what happened in this city" lives on this object — there is no
 * other public way to read incidents.
 */
export interface LoadedFeed {
  city: CityKey;
  ready: boolean;
  generatedAt: string | null;
  cityLabel: string | null;
  /** Which pipeline served this feed: the truth engine (trust-contract
   *  items) or the RSS news worker. Null when nothing loaded. */
  feedOrigin: "engine" | "news" | null;
  /** What the truth engine said when we asked it (engine-fed cities only):
   *    "ok"          — engine answered with signals (feedOrigin "engine")
   *    "quiet"       — engine answered, zero signals; news is the fallback
   *    "unreachable" — engine fetch failed; news is the fallback
   *  Null for cities that don't have an engine feed yet. The pill uses
   *  this to say honestly WHY Sydney is showing news reports. */
  engineState: "ok" | "quiet" | "unreachable" | null;
  rawItems: ReadonlyArray<RawIncident>;
  rawExtras: ReadonlyArray<RawIncident>;
  rawAll: ReadonlyArray<RawIncident>;
  deduped: ReadonlyArray<DedupedIncident>;
  dedupedCautionary: ReadonlyArray<DedupedIncident>;
  dedupedPositive: ReadonlyArray<DedupedIncident>;
  /** Parallel city-anchored video pool — see worker `nearby_videos`.
   *  These are YT items whose title/description match a city keyword
   *  but NOT a specific suburb. They're anchored at the city centroid
   *  with `is_city_anchored: true` so the bubble layer ignores them
   *  while the near-me panel surfaces them via haversine distance from
   *  the user's GPS fix. Intentionally NOT included in `rawAll` /
   *  `deduped` so existing map-pin consumers see no behaviour change. */
  nearbyVideos: ReadonlyArray<RawIncident>;
}

/* -------------------------------------------------------------------------- */
/*  Cache                                                                     */
/* -------------------------------------------------------------------------- */

/** TTL for a good feed — matches the engine's `*\/15` ingest/pulse crons;
 *  refetching faster would only re-read identical rows. */
const FEED_TTL_MS = 15 * 60_000;
/** TTL for the failure sentinel — long enough to stop a render loop from
 *  hammering a down worker, short enough that "live" recovers without a
 *  page reload. A transient failure must never poison the session. */
const FAILURE_TTL_MS = 30_000;

const STATIC_SNAPSHOT_BASE =
  (globalThis as { HYPERLOCAL_STATIC_SNAPSHOT_BASE?: string })
    .HYPERLOCAL_STATIC_SNAPSHOT_BASE ??
  (import.meta as ImportMeta & {
    env?: { VITE_HYPERLOCAL_STATIC_SNAPSHOT_BASE?: string };
  }).env?.VITE_HYPERLOCAL_STATIC_SNAPSHOT_BASE ??
  "/api/live-snapshots";

function staticSnapshotUrl(city: CityKey): string {
  return `${STATIC_SNAPSHOT_BASE.replace(/\/+$/, "")}/incidents-${city}.json`;
}

interface CacheEntry {
  feed: LoadedFeed;
  fetchedAt: number;
}

const _cache: Map<CityKey, CacheEntry> = new Map();
const _inFlight: Map<CityKey, Promise<LoadedFeed>> = new Map();

function isFresh(entry: CacheEntry): boolean {
  const ttl = entry.feed.ready ? FEED_TTL_MS : FAILURE_TTL_MS;
  return Date.now() - entry.fetchedAt < ttl;
}

const EMPTY_FEED = (
  city: CityKey,
  engineState: LoadedFeed["engineState"] = null,
): LoadedFeed => ({
  city,
  ready: false,
  generatedAt: null,
  cityLabel: null,
  feedOrigin: null,
  engineState,
  rawItems: [],
  rawExtras: [],
  rawAll: [],
  nearbyVideos: [],
  deduped: [],
  dedupedCautionary: [],
  dedupedPositive: [],
});

/* -------------------------------------------------------------------------- */
/*  Verification logging                                                      */
/* -------------------------------------------------------------------------- */

function logDedupSummary(
  city: CityKey,
  rawAll: ReadonlyArray<RawIncident>,
  deduped: ReadonlyArray<DedupedIncident>,
): void {
  const rawBySuburb = new Map<string, number>();
  for (const it of rawAll) {
    rawBySuburb.set(it.suburb_id, (rawBySuburb.get(it.suburb_id) ?? 0) + 1);
  }
  const dedupedBySuburb = new Map<string, number>();
  for (const ev of deduped) {
    dedupedBySuburb.set(ev.suburb_id, (dedupedBySuburb.get(ev.suburb_id) ?? 0) + 1);
  }
  const suburbs = Array.from(
    new Set([...rawBySuburb.keys(), ...dedupedBySuburb.keys()]),
  );
  const perSuburb = suburbs
    .map((sub) => {
      const raw = rawBySuburb.get(sub) ?? 0;
      const dd = dedupedBySuburb.get(sub) ?? 0;
      return { suburb: sub, raw, deduped: dd, collapsed: raw - dd };
    })
    .sort((a, b) => b.raw - a.raw);

  const collapsed = rawAll.length - deduped.length;
  console.log(
    `[HYPERLOCAL/dedup] city=${city} raw=${rawAll.length} deduped=${deduped.length} collapsed=${collapsed}`,
    { perSuburb },
  );
  if (collapsed > 0) {
    for (const row of perSuburb) {
      if (row.collapsed > 0) {
        console.log(
          `[HYPERLOCAL/dedup]   ${row.suburb}: raw=${row.raw} deduped=${row.deduped} (collapsed ${row.collapsed})`,
        );
      }
    }
  }
}

/* -------------------------------------------------------------------------- */
/*  Public API                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Load (or return cached-and-fresh) deduplicated incidents for `city`.
 *
 * This is the ONLY public entry point for reading incidents in the app.
 * Other modules MUST NOT call `fetch('/api/incidents-…')` directly.
 */
export function loadIncidents(city: CityKey): Promise<LoadedFeed> {
  const entry = _cache.get(city);
  if (entry && isFresh(entry)) return Promise.resolve(entry.feed);
  const inFlight = _inFlight.get(city);
  if (inFlight) return inFlight;
  return startLoad(city);
}

/**
 * Bypass the TTL and fetch a fresh feed NOW (the in-place retry on the
 * feed pill, a manual refresh affordance). The swap is atomic: the cached
 * feed keeps serving every other surface until the new fetch succeeds,
 * and a failed refresh never evicts good data — it returns the previous
 * feed so the caller renders the truth we still have.
 */
export function refreshIncidents(city: CityKey): Promise<LoadedFeed> {
  // Piggyback on an in-flight load — two surfaces pressing retry at the
  // same moment must not double-fetch.
  const inFlight = _inFlight.get(city);
  if (inFlight) return inFlight;
  return startLoad(city);
}

/**
 * Cache-write policy, shared by load + refresh:
 *   - a ready feed always replaces whatever was cached (atomic swap);
 *   - a failure sentinel is cached (30s TTL via isFresh) ONLY when there
 *     is no previous good feed — a transient failure must not blank a
 *     map that already has real data. Returns the feed callers should see.
 */
function commitFeed(city: CityKey, feed: LoadedFeed): LoadedFeed {
  const prev = _cache.get(city);
  if (!feed.ready && prev?.feed.ready) {
    console.warn(
      `[HYPERLOCAL/incidents] city=${city} refresh failed — keeping previous feed (fetched ${new Date(prev.fetchedAt).toISOString()})`,
    );
    return prev.feed;
  }
  _cache.set(city, { feed, fetchedAt: Date.now() });
  return feed;
}

function startLoad(city: CityKey): Promise<LoadedFeed> {
  const p: Promise<LoadedFeed> = (async () => {
    try {
      const feed = await fetchFeed(city);
      return commitFeed(city, feed);
    } finally {
      _inFlight.delete(city);
    }
  })();
  _inFlight.set(city, p);
  return p;
}

/** One network pass — no cache reads or writes. Never throws. */
async function fetchFeed(city: CityKey): Promise<LoadedFeed> {
  // Engine verdict for this pass — set inside the fetch closure below,
  // read when assembling the result (and the failure sentinel).
  let engineState: LoadedFeed["engineState"] = null;
  try {
    // Single endpoint: the Cloudflare Worker reads Postgres/PostGIS and
    // returns cache-control headers, so the browser can honor edge TTLs
    // without a second local fallback.
    let json: any = null;
    // Fire the incidents (Worker) and Facebook (static asset) requests
    // in parallel — they're independent endpoints with independent
    // failure modes. Promise.all + a defensive .catch on FB so a
    // Facebook outage never affects the load-bearing incidents path.
    // FB items are merged into rawAll BEFORE deduplication so cross-
    // source dupes (a Police FB post + a 9News article about the same
    // incident) collapse into one cluster naturally.
    const tryFetch = async (url: string): Promise<any | null> => {
      try {
        const res = await fetch(url, {
          // Default cache policy honors the Worker's max-age headers.
          // Don't force-cache (would hide updates within the TTL) and
          // don't no-cache (would skip the browser cache the Worker
          // explicitly asked for).
          signal: AbortSignal.timeout?.(8000),
        });
        if (res.ok) return await res.json();
        console.warn(
          `[HYPERLOCAL/incidents] city=${city} HTTP ${res.status} from ${url}`,
        );
        return null;
      } catch (err) {
        console.warn(
          `[HYPERLOCAL/incidents] city=${city} fetch failed (${url}):`,
          err,
        );
        return null;
      }
    };

    const [incidentsRes, facebookItems] = await Promise.all([
      (async () => {
        // Engine-indexed cities read the truth engine FIRST — its items
        // carry the trust contract (confidence, evidence status, source
        // tier). Fall back to the RSS cron worker when the engine is
        // unreachable or has nothing yet (a thin engine day must not
        // blank the map when news exists).
        if (ENGINE_FEED_CITIES.has(city)) {
          const engine = await tryFetch(
            `${engineBase()}/api/incidents-live/${city}`,
          );
          const engineItemCount =
            (Array.isArray(engine?.items) ? engine.items.length : 0) +
            (Array.isArray(engine?.extras) ? engine.extras.length : 0);
          if (engine && engineItemCount > 0) {
            engineState = "ok";
            engine.__origin = "engine";
            return engine;
          }
          // Two distinct truths, two distinct lines: an engine that
          // answered "nothing right now" is healthy; an engine we
          // couldn't reach is an outage. The pill copy depends on which.
          if (engine) {
            engineState = "quiet";
            console.log(
              `[HYPERLOCAL/incidents] city=${city} engine reachable but quiet (0 items) — falling back to news worker`,
            );
          } else {
            engineState = "unreachable";
            console.warn(
              `[HYPERLOCAL/incidents] city=${city} engine unreachable — falling back to news worker`,
            );
          }
        }
        const cron = await tryFetch(`/api/incidents-live/${city}`);
        if (cron) {
          cron.__origin = "news";
          return cron;
        }

        const snapshot = await tryFetch(staticSnapshotUrl(city));
        if (snapshot) {
          snapshot.__origin = "engine";
          snapshot.__static_snapshot_fallback = true;
          console.warn(
            `[HYPERLOCAL/incidents] city=${city} live API unavailable - using static snapshot fallback`,
          );
          return snapshot;
        }

        return null;
      })(),
      // fetchFacebookItems never throws — returns [] on any failure.
      fetchFacebookItems(city),
    ]);
    json = incidentsRes;

    if (!json) {
      // Engine (if any) AND news worker both failed — the sentinel
      // carries the engine verdict so the pill can say what broke.
      return EMPTY_FEED(city, engineState);
    }

    const rawItems = Array.isArray(json.items) ? json.items : [];
    const rawExtras = Array.isArray(json.extras) ? json.extras : [];
    // Engine items carry their context text as `body`; the cron feed uses
    // `summary`. Normalise so every card reads one field. Never overwrite
    // an existing summary.
    if (json.__origin === "engine") {
      for (const it of [...rawItems, ...rawExtras]) {
        if (it && typeof it.body === "string" && !it.summary) {
          it.summary = it.body;
        }
      }
    }
    // Facebook items join the same pool that goes through dedup.
    // When facebookItems is [] (no token, empty pages, fetch failed)
    // rawAll is bit-identical to the pre-FB behaviour.
    const rawAll: RawIncident[] = [
      ...rawItems,
      ...rawExtras,
      ...facebookItems,
    ];
    // Parallel city-anchored video pool. Read from the worker's new
    // `nearby_videos` field; tolerate absence (older worker / empty
    // pass) by falling back to []. Intentionally NOT folded into
    // rawAll: the bubble/safety/dedup pipeline must continue to see
    // only suburb-anchored items, so map pins never land at a fake
    // location. The near-me panel reads `nearbyVideos` directly and
    // unions it into its own ranking.
    const nearbyVideos: RawIncident[] = Array.isArray(json.nearby_videos)
      ? json.nearby_videos
      : [];
    const deduped = deduplicateIncidents(rawAll);
    const dedupedCautionary = deduped.filter(
      (e) => e.category === "cautionary",
    );
    const dedupedPositive = deduped.filter(
      (e) => e.category === "positive",
    );

    const feed: LoadedFeed = {
      city,
      ready: true,
      generatedAt:
        typeof json.generated_at === "string" ? json.generated_at : null,
      cityLabel:
        typeof json.city_label === "string" ? json.city_label : null,
      feedOrigin: json.__origin === "engine" ? "engine" : "news",
      engineState,
      rawItems,
      rawExtras,
      rawAll,
      nearbyVideos,
      deduped,
      dedupedCautionary,
      dedupedPositive,
    };
    // Worker also tells us where the data came from — log it once
    // per fetch so a reviewer can prove the pipeline tier.
    const source = typeof json._source === "string" ? json._source : "unknown";
    console.log(
      `[HYPERLOCAL/incidents] city=${city} loaded via ${feed.feedOrigin} (_source=${source}, items=${rawItems.length}, extras=${rawExtras.length}, nearby_videos=${nearbyVideos.length}, facebook=${facebookItems.length})`,
    );
    logDedupSummary(city, rawAll, deduped);
    return feed;
  } catch (err) {
    console.warn(`[HYPERLOCAL/dedup] city=${city} load failed`, err);
    return EMPTY_FEED(city, engineState);
  }
}

/**
 * Synchronous read for callers that already know the load completed.
 * Returns the empty sentinel if no load has been kicked off yet.
 */
export function getLoadedIncidents(city: CityKey): LoadedFeed {
  return _cache.get(city)?.feed ?? EMPTY_FEED(city);
}

/**
 * Resolves once the in-flight load (if any) settles. Useful for boot
 * sequencing — main-simple.ts can `await whenIncidentsReady(city)`.
 */
export function whenIncidentsReady(city: CityKey): Promise<void> {
  const cached = _cache.get(city);
  if (cached) return Promise.resolve();
  return _inFlight.get(city)?.then(() => undefined) ?? Promise.resolve();
}
